import type { TrackingCallback } from '@growthbook/growthbook'

import type { ExperimentViewedHandler, ExperimentViewedPayload } from './types'

/** Exposures already reported on this page, across every instance. */
const reported = new Set<string>()

export interface ExposureTracker {
  /** Passed to the SDK at construction, whether or not a handler exists yet. */
  callback: TrackingCallback
  /** Sets (or swaps) the handler and delivers exposures held until now. */
  setHandler: (handler: ExperimentViewedHandler | undefined) => void
}

/**
 * Adapts GrowthBook's tracking callback to the library's exposure payload,
 * for bucketed visitors only.
 *
 * Exposures that arrive before a handler (Avo initialising asynchronously)
 * are held and delivered once one is set: the SDK marks an experiment tracked
 * on its first evaluation and never calls back for it again, so dropping it
 * would lose it for good.
 *
 * The SDK dedupes per instance only. Instances are per owner, so a reopened
 * panel or a StrictMode remount gets a fresh one; deduping here, per app,
 * visitor, experiment and variation, keeps one exposure per page. A new id
 * (consent given) is a new visitor and reports again.
 */
export const createExposureTracker = (
  applicationId: string,
  initialHandler?: ExperimentViewedHandler,
): ExposureTracker => {
  let handler = initialHandler
  const held = new Map<string, ExperimentViewedPayload>()

  const deliver = (
    report: ExperimentViewedHandler,
    key: string,
    payload: ExperimentViewedPayload,
  ) => {
    if (reported.has(key)) return
    reported.add(key)
    report(payload)
  }

  return {
    callback: (experiment, result) => {
      if (!result.inExperiment) return

      const key = JSON.stringify([
        applicationId,
        result.hashValue,
        experiment.key,
        result.key,
      ])
      const payload = { experimentId: experiment.key, variationId: result.key }
      if (handler) deliver(handler, key, payload)
      else held.set(key, payload)
    },
    setHandler: next => {
      handler = next
      if (!next) return
      for (const [key, payload] of held) deliver(next, key, payload)
      held.clear()
    },
  }
}
